import React, { useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Pencil, Trash2, Clock, MapPin, Send } from 'lucide-react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

const TimesheetScheduleTable = ({
  schedules,
  isAdmin,
  onSave,
  onDelete,
  setEditingSchedule,
  setIsDialogOpen,
  selectedIds,
  setSelectedIds
}) => {
  // Filter only unpublished schedules for selection purposes (since we only bulk publish)
  const publishableSchedules = useMemo(() => {
    return schedules.filter((s) => s.status !== 'Published');
  }, [schedules]);

  const allPublishableSelected =
    publishableSchedules.length > 0 && publishableSchedules.every((s) => selectedIds.has(s._id));

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds((prevSelected) => {
        // eslint-disable-next-line no-undef
        const newSelected = new Set(prevSelected);
        publishableSchedules.forEach((s) => newSelected.add(s._id));
        return newSelected;
      });
    } else {
      setSelectedIds((prevSelected) => {
        // eslint-disable-next-line no-undef
        const newSelected = new Set(prevSelected);
        publishableSchedules.forEach((s) => newSelected.delete(s._id));
        return newSelected;
      });
    }
  };

  const handleSelectOne = (id, checked) => {
    setSelectedIds((prevSelected) => {
      // eslint-disable-next-line no-undef
      const newSelected = new Set(prevSelected);
      if (checked) {
        newSelected.add(id);
      } else {
        newSelected.delete(id);
      }
      return newSelected;
    });
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setIsDialogOpen(true);
  };

  const MobileCard = ({ schedule }) => (
    <div className="tw-block md:tw-hidden tw-bg-card tw-border tw-border-border tw-rounded-lg tw-p-4 tw-mb-4 tw-shadow-sm">
      <div className="tw-flex tw-justify-between tw-items-start tw-mb-3">
        <div className="tw-flex tw-items-center tw-gap-3">
          <Avatar className="tw-h-10 tw-w-10">
            <AvatarFallback className="tw-bg-secondary tw-text-secondary-foreground tw-font-bold">
              {schedule?.DisplayName?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="tw-font-semibold tw-text-card-foreground tw-m-0">{schedule?.DisplayName || 'Unknown'}</h3>
            <p className="tw-text-xs tw-text-muted-foreground tw-m-0">{schedule?.jobTitle || 'No Role'}</p>
          </div>
        </div>
        {isAdmin && (
          <div className="tw-flex tw-gap-1">
            <Button variant="ghost" size="icon" className="tw-h-8 tw-w-8" onClick={() => handleEdit(schedule)}>
              <Pencil className="tw-h-4 tw-w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-8 tw-w-8 tw-text-destructive"
              onClick={() => onDelete?.(schedule._id)}
            >
              <Trash2 className="tw-h-4 tw-w-4" />
            </Button>
          </div>
        )}
      </div>

      <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-text-sm">
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-muted-foreground">
          <MapPin className="tw-h-3.5 tw-w-3.5" />
          <Badge variant="secondary" className="tw-font-medium tw-truncate">
            {schedule.site?.name || 'No Site'}
          </Badge>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-muted-foreground">
          <Clock className="tw-h-3.5 tw-w-3.5" />
          <span>
            {schedule.startTime} - {schedule.endTime}
          </span>
        </div>
      </div>

      {schedule.comments && (
        <div className="tw-mt-3 tw-text-xs tw-text-muted-foreground tw-bg-muted/30 tw-p-2 tw-rounded tw-italic">
          {schedule.comments}
        </div>
      )}
    </div>
  );

  return (
    <div className="tw-space-y-4">
      {/* Desktop Table View */}
      <div className="tw-hidden md:tw-block tw-rounded-lg tw-border tw-border-border tw-bg-card tw-overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="tw-border-border tw-bg-muted/50">
              {isAdmin && (
                <TableHead className="tw-w-12">
                  <Checkbox
                    checked={allPublishableSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                  />
                </TableHead>
              )}
              <TableHead className="tw-text-muted-foreground tw-font-semibold">DisplayName</TableHead>
              <TableHead className="tw-text-muted-foreground tw-font-semibold">Site</TableHead>
              <TableHead className="tw-text-muted-foreground tw-font-semibold">Work Type</TableHead>
              <TableHead className="tw-text-muted-foreground tw-font-semibold">Date</TableHead>
              <TableHead className="tw-text-muted-foreground tw-font-semibold">Time Range</TableHead>
              <TableHead className="tw-text-muted-foreground tw-font-semibold">Comments</TableHead>
              {isAdmin && <TableHead className="tw-text-muted-foreground tw-font-semibold">Status</TableHead>}
              {isAdmin && (
                <TableHead className="tw-w-24 tw-text-right tw-text-muted-foreground tw-font-semibold">
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 4} className="tw-h-32 tw-text-center tw-text-muted-foreground">
                  No schedules found for today.
                </TableCell>
              </TableRow>
            ) : (
              schedules.map((schedule) => (
                <TableRow
                  key={schedule._id}
                  className="tw-border-border hover:tw-bg-muted/30 tw-transition-colors"
                  data-state={selectedIds.has(schedule._id) && 'selected'}
                >
                  {isAdmin && (
                    <TableCell>
                      {schedule.status !== 'Published' && (
                        <Checkbox
                          checked={selectedIds.has(schedule._id)}
                          onCheckedChange={(checked) => handleSelectOne(schedule._id, checked)}
                          aria-label="Select row"
                        />
                      )}
                    </TableCell>
                  )}
                  {/* employee displayname */}
                  <TableCell>
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <div className="tw-flex tw-flex-col">
                        <span className="tw-font-medium tw-text-card-foreground">
                          {schedule?.DisplayName || 'Unknown'}
                        </span>
                        <span className="tw-text-xs tw-text-muted-foreground">{schedule?.jobTitle}</span>
                      </div>
                    </div>
                  </TableCell>

                  {/* site */}
                  <TableCell>
                    <Badge variant="outline" className="tw-font-medium" title={schedule.site?.name}>
                      {schedule.site?.name || '-'}
                    </Badge>
                  </TableCell>

                  {/* work type */}
                  <TableCell>
                    <Badge variant="outline" className="tw-font-medium" title={schedule.workType}>
                      {schedule.workType || '-'}
                    </Badge>
                  </TableCell>

                  {/* date */}
                  <TableCell>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-mono">
                      <span>{format(new Date(schedule.date), 'dd/MM/yyyy')}</span>
                    </div>
                  </TableCell>

                  {/* start and end time */}
                  <TableCell>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-mono">
                      <span>{schedule.startTime}</span>
                      <span className="tw-text-muted-foreground">-</span>
                      <span>{schedule.endTime}</span>
                    </div>
                  </TableCell>

                  {/* comments */}
                  <TableCell
                    className="tw-max-w-[300px] tw-whitespace-pre-wrap tw-break-words tw-text-muted-foreground tw-text-sm"
                    title={schedule.comments}
                  >
                    {schedule.comments || '--'}
                  </TableCell>

                  {/* status */}
                  {isAdmin && (
                    <TableCell className="tw-max-w-[200px] tw-truncate tw-text-muted-foreground tw-text-sm">
                      {schedule.status || '--'}
                    </TableCell>
                  )}

                  {/* actions */}
                  {isAdmin && (
                    <TableCell>
                      <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                        {schedule.status !== 'Published' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="tw-h-8 tw-w-8 hover:tw-bg-blue-100 tw-text-blue-600"
                            title="Publish Schedule"
                            onClick={() => onSave({ ...schedule, id: schedule._id, status: 'Published' })}
                          >
                            <Send className="tw-h-4 tw-w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="tw-h-8 tw-w-8 hover:tw-bg-secondary"
                          onClick={() => handleEdit(schedule)}
                        >
                          <Pencil className="tw-h-4 tw-w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="tw-h-8 tw-w-8 hover:tw-bg-destructive/10 tw-text-destructive"
                          onClick={() => onDelete(schedule._id)}
                        >
                          <Trash2 className="tw-h-4 tw-w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="md:tw-hidden">
        {schedules.length === 0 ? (
          <div className="tw-text-center tw-py-12 tw-text-muted-foreground tw-border tw-border-dashed tw-rounded-lg tw-bg-muted/20">
            No schedules found for today.
          </div>
        ) : (
          schedules.map((schedule) => <MobileCard key={schedule._id} schedule={schedule} />)
        )}
      </div>
    </div>
  );
};

TimesheetScheduleTable.propTypes = {
  schedules: PropTypes.any,
  isAdmin: PropTypes.any,
  onSave: PropTypes.any,
  onDelete: PropTypes.any,
  onBulkPublish: PropTypes.func,
  editingSchedule: PropTypes.any,
  setEditingSchedule: PropTypes.any,
  isDialogOpen: PropTypes.any,
  setIsDialogOpen: PropTypes.any,
  selectedIds: PropTypes.any,
  setSelectedIds: PropTypes.any,
  schedule: PropTypes.any
};

export default TimesheetScheduleTable;
